import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTripContext } from './TripDetailPage';
import { tipsApi } from '../../api/tips.api';
import { useAiDataVersion, useAiPanel, useAiPanelState } from '../../components/ai/AiPanelContext';
import type { AiPersistedAction } from '../../api/aiProposals';
import type { Tip } from '../../types';
import { toast } from '../../components/ui/Toast';
import { isAlreadyGone } from '../../api/client';
import TripEssentialsPanel from './info/TripEssentialsPanel';
import EntryRequirementsPanel from './info/EntryRequirementsPanel';
import InfoSectionHeader from './info/InfoSectionHeader';
import AgencyCard from '../../components/agency/AgencyCard';
import {
  Trash2, Tag, Plus, Icon, Lightbulb,
} from '../../components/ui/Icon';

const TIP_COLORS = [
  'bg-teal-50   border-teal-100   text-teal-700   dark:bg-teal-900/20   dark:border-teal-800   dark:text-teal-300',
  'bg-violet-50 border-violet-100 text-violet-700 dark:bg-violet-900/20 dark:border-violet-800 dark:text-violet-300',
  'bg-amber-50  border-amber-100  text-amber-700  dark:bg-amber-900/20  dark:border-amber-800  dark:text-amber-300',
  'bg-sky-50    border-sky-100    text-sky-700    dark:bg-sky-900/20    dark:border-sky-800    dark:text-sky-300',
  'bg-pink-50   border-pink-100   text-pink-700   dark:bg-pink-900/20   dark:border-pink-800   dark:text-pink-300',
  'bg-green-50  border-green-100  text-green-700  dark:bg-green-900/20  dark:border-green-800  dark:text-green-300',
];

export default function InfoTab() {
  const { trip } = useTripContext();
  const { t } = useTranslation();
  const [tips, setTips]             = useState<Tip[]>([]);
  const [loading, setLoading]       = useState(true);

  // These chips get saved as the tip's category, so they follow the trip's
  // content language — not the reader's, or each traveller would file the same
  // tip under a different heading.
  const suggestedTipCats = t('info.tipCategories', {
    returnObjects: true, lng: trip.contentLanguage,
  }) as string[];

  // Tips proposals accepted in the docked gIA panel land here.
  useAiPanel({ context: 'tips', onAction: handleTipsAiAction });
  // Drop to 2 columns while the docked panel narrows the content (see VenuesTab).
  const { collapsed: aiCollapsed } = useAiPanelState();
  function handleTipsAiAction(actions: AiPersistedAction[]) {
    for (const a of actions) {
      if (a.type === 'tips_created') setTips(prev => [...prev, ...a.data]);
    }
  }

  const [newTipCat, setNewTipCat]             = useState('');
  const [newTipContent, setNewTipContent]     = useState('');

  // ADR-0028: re-read when an accepted card changed a tip.
  const aiTipsVersion = useAiDataVersion('tips');
  useEffect(() => {
    tipsApi.list(trip.id)
      .then(setTips)
      .catch(() => toast(t('info.loadFailed'), 'error'))
      .finally(() => setLoading(false));
  }, [trip.id, aiTipsVersion]);

  async function addTip() {
    // Wait for the list: an optimistic add before it lands gets clobbered by it.
    if (loading || !newTipCat.trim() || !newTipContent.trim()) return;
    const tip = await tipsApi.create(trip.id, { category: newTipCat.trim(), content: newTipContent.trim() });
    setTips(t => [...t, tip]);
    setNewTipCat('');
    setNewTipContent('');
  }

  async function deleteTip(tipId: string) {
    try {
      await tipsApi.delete(trip.id, tipId);
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    setTips(t => t.filter(x => x.id !== tipId));
  }

  const tipsByCategory = tips.reduce((acc, t) => {
    (acc[t.category] ??= []).push(t);
    return acc;
  }, {} as Record<string, Tip[]>);

  return (
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">
      <div className="shrink-0 px-4 md:px-8 pt-5 pb-4 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-slate-400 mb-0.5">
            {trip.destination.toUpperCase()}
          </p>
          <h2 className="font-serif italic font-semibold text-2xl md:text-3xl text-slate-900 dark:text-slate-100">
            {t('info.heading')}
          </h2>
        </div>
      </div>
      </div>{/* /header panel */}
      <div className="flex-1 overflow-auto px-4 md:px-8 py-5 md:py-6">

      {/* Entry requirements (visa/vaccines/passport) + country practicals — the
          high-value, personalised travel info. Tips (below) stay as the soft layer. */}
      {/* ADR-0033: in the sidebar too, but that is desktop-only. */}
      {trip.agency && <AgencyCard agency={trip.agency} />}
      <EntryRequirementsPanel tripId={trip.id} />
      <TripEssentialsPanel tripId={trip.id} />

      <section>
        <InfoSectionHeader icon={<Icon as={Lightbulb} size={17} />} title={t('info.tipsSectionTitle')} />

        {/* Add tip — input + action unified into one control so the "pick a
            category, write the tip, then add" flow reads as a single step,
            mirroring PackingTab's add-section control. Button stays disabled
            until both fields have content, so it never looks tappable while
            there's nothing to add. */}
        <div className="mb-5">
          <label htmlFor="new-tip" className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1.5">
            {t('info.addTipLabel')}
          </label>
          <div className="flex items-stretch rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden focus-within:border-teal-400 dark:focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-400/30 transition-all flex-wrap sm:flex-nowrap">
            <input
              type="text"
              className="w-full sm:w-36 shrink-0 bg-transparent px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none border-b sm:border-b-0 sm:border-r border-slate-100 dark:border-slate-800"
              placeholder={t('info.addTipCategoryPlaceholder')}
              value={newTipCat}
              onChange={e => setNewTipCat(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addTip()}
            />
            <input
              id="new-tip"
              type="text"
              className="flex-1 min-w-0 bg-transparent px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none"
              placeholder={t('info.addTipContentPlaceholder')}
              value={newTipContent}
              onChange={e => setNewTipContent(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addTip()}
            />
            <button
              onClick={addTip}
              disabled={loading || !newTipCat.trim() || !newTipContent.trim()}
              className="shrink-0 my-1 mr-1 px-4 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon as={Plus} size={16} />
              {t('info.addTipBtn')}
            </button>
          </div>
        </div>

        {!loading && Object.keys(tipsByCategory).length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-6 mb-4">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">{t('info.suggestedCategories')}</p>
            <div className="flex flex-wrap gap-2">
              {suggestedTipCats.map(cat => (
                <button
                  key={cat}
                  onClick={() => setNewTipCat(cat)}
                  className="text-xs px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-700 dark:hover:text-teal-400 transition-colors cursor-pointer"
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={`grid grid-cols-1 sm:grid-cols-2 ${aiCollapsed ? 'lg:grid-cols-3' : ''} gap-4 mb-5`}>
          {Object.entries(tipsByCategory).map(([cat, catTips], catIdx) => (
            <div key={cat} className="card p-5">
              <div className="flex items-center gap-2 mb-3">
                <Icon as={Tag} size={14} />
                <h3 className="font-semibold text-sm text-slate-700 dark:text-slate-300">{cat}</h3>
                <span className="badge-slate ml-auto">{catTips.length}</span>
              </div>
              <div className="space-y-2">
                {catTips.map(tip => (
                  <div
                    key={tip.id}
                    className={`group flex items-start gap-2.5 px-3 py-2.5 rounded-xl border ${TIP_COLORS[catIdx % TIP_COLORS.length]}`}
                  >
                    <div className="w-1 h-1 rounded-full mt-2 bg-current opacity-50 shrink-0" />
                    <p className="text-sm flex-1 leading-relaxed">{tip.content}</p>
                    <button
                      onClick={() => deleteTip(tip.id)}
                      className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-current opacity-50 hover:opacity-100 hover:text-red-500 transition-opacity cursor-pointer"
                      aria-label={t('info.deleteTip')}
                    >
                      <Icon as={Trash2} size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

      </section>

      </div>
    </div>
  );
}
